import {
  AdyenCheckout,
  ApplePay,
  CoreConfiguration,
  Dropin,
  GooglePay,
  PayPal,
  Card,
  Klarna,
  SepaDirectDebit,
  PaymentMethodsConfiguration,
  PaymentAmount,
  ResultCode,
  PaymentAction,
  PaymentData,
  AdditionalDetailsData,
  SubmitActions,
} from '@adyen/adyen-web';
import {
  ApiError,
  ApiRequestPaymentDropinPay,
  ApiRequestPaymentDropinPaymentDetails,
  ApiResponsePaymentDropinPay,
  ApiResponsePaymentDropinPaymentDetails,
  ApiResponsePaymentDropinPaymentMethods,
  ApiServiceResponse,
  IApiService,
} from '../api';
import {
  AvailableProducts,
  ProductInfoV2,
  Session,
  SessionInfo,
  SuccessfulPaymentInfo,
} from '../session';
import {PURCHASE_PAGE_CHECKOUT_VERIFY} from '../../config/paths';
import {User} from '../user';
import {
  ApiContractBlueprintGroupV3,
  ApiResponsePaymentGetContractBlueprintsV3,
  ContractBlueprint,
} from '../../../types/api/getContractBlueprints';
import {EnvConfig} from '../../config/envConfig.interface';

export interface GetProductsResponse {
  blueprintGroups: ApiContractBlueprintGroupWithPerkbase[];
  /**
   * return information which blueprint variant was eventually used.
   * This way we can determine if the code was valid
   */
  variantUsed: string;
}

export interface ApiContractBlueprintGroupWithPerkbase
  extends ApiContractBlueprintGroupV3 {
  perkBasePrice?: number;
  contractBlueprints: ApiContractBlueprintWithPerk[];
}

export interface ApiContractBlueprintWithPerk extends ContractBlueprint {
  perkPercent?: number;
}

export type AdyenMakePaymentResult =
  | {
      complete: true;
      clientAction: 'none';
      resultCode: ResultCode;
      action?: PaymentAction;
    }
  | {
      complete: false;
      clientAction: 'provideDetails';
      resultCode: ResultCode;
      action?: PaymentAction;
    };

export interface IPurchaseService {
  hasProductSelected: boolean;
  getProducts(
    bpVariantOverride: string | null,
    overrideForCompany?: boolean,
  ): Promise<GetProductsResponse>;
  setupAdyen(config: {
    dropinHtmlNode: HTMLElement;
    onSubmitCallback: ({
      data,
      actions,
    }: {
      data: PaymentData;
      actions: SubmitActions;
    }) => Promise<void>;
    onDetailsSubmitCallback: ({
      data,
      actions,
    }: {
      data: AdditionalDetailsData['data'];
      actions: SubmitActions;
    }) => Promise<void>;
  }): Promise<Dropin>;
  adyenMakePayment(
    data: PaymentData,
    invoiceCountryCode: string,
  ): Promise<AdyenMakePaymentResult>;
  adyenProvideDetails(
    data: AdditionalDetailsData['data'],
    blueprintVariantOverride?: string,
    requestContractDetailsId?: string,
  ): Promise<AdyenMakePaymentResult>;
  keepAwakeLoop(): Promise<void>;
}

export class Purchase implements IPurchaseService {
  constructor(
    protected configuration: EnvConfig,
    protected api: IApiService,
  ) {}
  private keepAwakeLoopRunning = false;

  public get hasProductSelected(): boolean {
    const productInfo = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    const blueprintGroupId = productInfo?.blueprintGroupId;
    const blueprintId = productInfo?.blueprintId;

    return !!blueprintGroupId && !!blueprintId;
  }

  /**
   * Fetches the available products (blueprintGroups and blueprints) for the user.
   * Also respects special offers the user might can get provided by his company.
   * Might return cached results.
   *
   * By default the function uses the default blueprint variant (stored in the config file).
   * When the user is in a company and this company provides a different default bp variant, this one will be used.
   * If [bpVariantOverride] is submitted, this value will always be used.
   * If there are no products available for the given variant the function will fallback
   * to the company variant or default variant.
   *
   * The function never uses the bp variant stored in the sessionStorage to not maintain a variant
   * to a later page visit. This has marketing reasons; the page should always have the same standard behaviour
   * unless specified else (e.g. by a custom variant in the URL).
   * If the variant in the sessionStorage should be used this needs to be submitted by the calling code.
   *
   * @param bpVariantOverride override for the blueprint variant to use
   * @param overrideForCompany if true, the override variant will also override the variant that might be specified
   * in the users company.
   */
  public async getProducts(
    bpVariantOverride: string | null = null,
    overrideForCompany = true,
  ): Promise<GetProductsResponse> {
    console.time('fetchProducts');
    let blueprintVariant = this.configuration.defaultBpVariant;
    let fetchCompany = true;

    if (bpVariantOverride) {
      if (overrideForCompany) {
        fetchCompany = false;
      }

      blueprintVariant = bpVariantOverride;
    }

    if (fetchCompany) {
      const company = await User.instance.currentUserCompany();
      if (company?.defaultContractBpVariant) {
        console.log('using company bp variant');
        blueprintVariant = company.defaultContractBpVariant;
      }
    }

    const cached = Session.instance.getObject<AvailableProducts>(
      SessionInfo.purchase_availableProducts,
    );
    if (cached) {
      console.log('waiting for uid...');
      console.time('waitingUid');
      const userId = (await User.instance.currentFirebaseAuthUser())?.uid;
      console.timeEnd('waitingUid');
      if (
        cached.userId &&
        cached.userId === userId &&
        cached.blueprintVariant === blueprintVariant
      ) {
        const oneHourAgo = new Date();
        oneHourAgo.setHours(oneHourAgo.getHours() - 1);
        // cached data must be created in the last hour
        if (cached.lastUpdate >= oneHourAgo.getTime()) {
          // return cached data
          console.log('return cached data');
          console.timeEnd('fetchProducts');
          return {
            blueprintGroups: cached.blueprintGroups,
            variantUsed: blueprintVariant,
          };
        }
      }
    }

    try {
      const contractBlueprintsResponse =
        await this.getContractBlueprints(blueprintVariant);

      const groups: ApiContractBlueprintGroupWithPerkbase[] =
        contractBlueprintsResponse.response.contractBlueprintGroups;

      // calc perk
      for (const group of groups) {
        // perk base = true means it's the product treated as the most expensive
        const perkBaseFilter = group.contractBlueprints.filter(x => x.perkBase);
        // we want to find the "perk base" product with the biggest price
        // so we can compare to it to display how much you saved.
        const perkBaseBlueprint =
          perkBaseFilter.length === 0
            ? null
            : perkBaseFilter.reduce((a, b) => {
                if (!a) return b;
                const priceA = a.price.discount
                  ? a.price.discount.normalPrice.displayPeriodPrice
                  : a.price.displayPeriod.periodSingleIntervalPrice;
                const priceB = b.price.discount
                  ? b.price.discount.normalPrice.displayPeriodPrice
                  : b.price.displayPeriod.periodSingleIntervalPrice;
                if (priceA === null) return b;
                if (priceB === null) return a;
                // return the product with the higher price
                return priceA > priceB ? a : b;
              });
        if (perkBaseBlueprint) {
          group.perkBasePrice =
            perkBaseBlueprint.price.discount?.normalPrice
              ?.displayPeriodSingleIntervalPrice ??
            perkBaseBlueprint.price.displayPeriod.periodSingleIntervalPrice;
        }

        if (group.perkBasePrice) {
          for (const blueprint of group.contractBlueprints) {
            // if the most expensive option costs 16.49 EUR / month,
            // and this one costs 7.49 EUR /month (e.g. 89.99 EUR for a year)
            // do
            // (1 - 749 / 1649) * 100
            // to calculate how much you save (percent) = 54%
            const perkPercentValue = Math.floor(
              (1 -
                blueprint.price.displayPeriod.periodSingleIntervalPrice /
                  group.perkBasePrice) *
                100,
            );
            if (perkPercentValue > 0) {
              blueprint.perkPercent = perkPercentValue;
            }
          }
        }
      }

      if (contractBlueprintsResponse.userId) {
        Session.instance.setObject<AvailableProducts>(
          SessionInfo.purchase_availableProducts,
          {
            blueprintVariant,
            userId: contractBlueprintsResponse.userId,
            blueprintGroups: groups,
            lastUpdate: Date.now(),
          },
        );
      }

      Session.instance.set(
        SessionInfo.purchase_blueprintVariant,
        blueprintVariant,
      );

      console.timeEnd('fetchProducts');
      return {blueprintGroups: groups, variantUsed: blueprintVariant};
    } catch (err) {
      if (err instanceof ApiError) {
        const apiErr = err as ApiError;

        if (
          apiErr.code === 'payment/unavailableVariant' ||
          apiErr.code === 'payment/unavailableCountry' ||
          (apiErr.code as string) === 'payment_unavailableVariant' ||
          (apiErr.code as string) === 'payment_unavailableCountry'
        ) {
          if (bpVariantOverride !== null) {
            // if you requested explicitly a variant that is not available,
            // pass the error to show a relevant error message
            throw err;
          }
          const defaultVariant = this.configuration.defaultBpVariant;
          if (blueprintVariant !== defaultVariant) {
            // retry

            if (fetchCompany) {
              // the current request was made using the company variant.
              // refetch using forced default variant
              console.log('retrying with default variant (forced)...');
              return this.getProducts(defaultVariant, true);
            } else {
              // in this case the current request was made with an overriden variant.
              // refetch with company variant (or default if none available)
              return this.getProducts(null);
            }
          }
        }
      }

      throw err;
    }
  }

  private async getContractBlueprints(
    blueprintVariant: string,
  ): Promise<ApiServiceResponse<ApiResponsePaymentGetContractBlueprintsV3>> {
    return await this.api.payment.getContractBlueprints(
      {
        includeNotPurchasable: false,
        allowAnonymous: true,
        blueprintVariant,
        provider: ['adyen'],
        languageTag: 'de',
        version: 300,
      },
      true,
    );
  }

  public async setupAdyen(config: {
    dropinHtmlNode: HTMLElement;
    /**
     * called when clicked on submit button before any other processing.
     * Trigger a loading state and then call [adyenMakePayment] from here!
     * Return without any action to stop further processing!
     */
    onSubmitCallback: ({
      data,
      actions,
    }: {
      data: PaymentData;
      actions: SubmitActions;
    }) => Promise<void>;
    /**
     * called when clicked on submit button (of payment details (e.g. 3DS2)) before any other processing.
     * Trigger a loading state and then call [adyenProvideDetails] from here!
     * Return without any action to stop further processing!
     */
    onDetailsSubmitCallback: ({
      data,
      actions,
    }: {
      data: AdditionalDetailsData['data'];
      actions: SubmitActions;
    }) => Promise<void>;
  }): Promise<Dropin> {
    const blueprintVariant = Session.instance.get(
      SessionInfo.purchase_blueprintVariant,
    );
    const productInfo = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    const blueprintGroupId = productInfo?.blueprintGroupId;
    const blueprintId = productInfo?.blueprintId;
    const periodPrice = productInfo?.price.period.periodPrice;

    if (
      !blueprintVariant ||
      !blueprintGroupId ||
      !blueprintId ||
      !periodPrice
    ) {
      throw new Error('no blueprint selected');
    }

    const environment = this.configuration;

    let paymentMethodsResponse: ApiResponsePaymentDropinPaymentMethods;

    try {
      paymentMethodsResponse = (
        await this.api.payment.dropin.paymentMethods(
          {
            blueprintVariant: blueprintVariant,
            blueprintGroup: blueprintGroupId,
            blueprintId: blueprintId,
            periodPrice: periodPrice,
            channel: environment.production ? 'Web' : 'Web-test',
          },
          true,
        )
      ).response;
    } catch (err) {
      if (err instanceof Error) {
        console.log('api failed', {...err});
      }
      throw err;
    }

    console.log('prepare resp', paymentMethodsResponse);

    if (!paymentMethodsResponse.success) {
      console.error('unsuccessful request');
      throw new Error('Payment currenty unavailable. Please try again later!');
    }

    if (
      paymentMethodsResponse.handler !== 'dropin' ||
      !paymentMethodsResponse.paymentMethods
    ) {
      throw new Error(
        "Unsupported handler. Currently only 'dropin' supported!",
      );
    }

    const payMethodsTranslated = paymentMethodsResponse.paymentMethods;

    payMethodsTranslated.paymentMethods.map(item => {
      switch (item.name) {
        case 'Cards':
        case 'Credit Card':
          item.name = 'Kreditkarte';
          break;
        case 'Online bank transfer.':
          item.name = 'Sofortüberweisung';
          break;
        case 'SEPA Direct Debit':
          item.name = 'SEPA Lastschrift';
          break;
      }

      return item;
    });

    console.log('translated', payMethodsTranslated);

    const removePaymentMethods: string[] = [];
    const paymentConfigs: PaymentMethodsConfiguration = {};

    removePaymentMethods.push('applepay');
    removePaymentMethods.push('paywithgoogle');

    let adyenAmount: PaymentAmount | null = null;
    if (paymentMethodsResponse.amount) {
      adyenAmount = paymentMethodsResponse.amount;
    } else {
      removePaymentMethods.push('paypal');
      removePaymentMethods.push('paywithgoogle');
      removePaymentMethods.push('applepay');
    }

    let adyenMerchant: string | null = null;
    if (paymentMethodsResponse.merchantAccount) {
      adyenMerchant = paymentMethodsResponse.merchantAccount;
    } else {
      removePaymentMethods.push('paywithgoogle');
    }

    // ----------

    if (!removePaymentMethods.includes('paypal') && adyenAmount) {
      paymentConfigs.paypal = {
        amount: adyenAmount,
        onError: (error, component) => {
          if (error.name === 'CANCEL') {
            component?.setStatus('ready');
          }
        },
      };
    }

    if (
      !removePaymentMethods.includes('paywithgoogle') &&
      adyenAmount &&
      adyenMerchant
    ) {
      paymentConfigs.paywithgoogle = {
        environment: environment.production ? 'PRODUCTION' : 'TEST',
        amount: adyenAmount,
        billingAddressRequired: false,
        configuration: {
          gatewayMerchantId: adyenMerchant,
        },
      };
    }

    paymentConfigs.card = {
      placeholders: {
        cardNumber: '1234 5678 9012 3456',
        expiryDate: 'MM/YY',
        securityCodeThreeDigits: '123',
      },
    };

    const adyenDropinConfig: CoreConfiguration = {
      countryCode: environment.languageArea.countryCode,
      locale: environment.languageArea.tag,
      environment: environment.production ? 'live' : 'test',
      clientKey: environment.adyen.clientKey,
      paymentMethodsResponse: payMethodsTranslated,
      translations: {
        'de-DE': {
          payButton: 'Jetzt kaufen',
        },
      },
      removePaymentMethods,

      onSubmit: (state, _, actions) =>
        config.onSubmitCallback({data: state.data, actions}),
      onAdditionalDetails: (state, _, actions) =>
        config.onDetailsSubmitCallback({data: state.data, actions}),
    };

    const checkout = await AdyenCheckout(adyenDropinConfig);
    return new Dropin(checkout, {
      paymentMethodComponents: [
        ApplePay,
        GooglePay,
        PayPal,
        Card,
        SepaDirectDebit,
        Klarna,
      ],
      paymentMethodsConfiguration: paymentConfigs,
    }).mount(config.dropinHtmlNode);
  }

  public async adyenMakePayment(
    data: PaymentData,
    invoiceCountryCode: string,
  ): Promise<AdyenMakePaymentResult> {
    const blueprintVariant = Session.instance.get(
      SessionInfo.purchase_blueprintVariant,
    );
    const productInfo = Session.instance.getObject<ProductInfoV2>(
      SessionInfo.purchase_productInfoV2,
    );
    const blueprintGroupId = productInfo?.blueprintGroupId;
    const blueprintId = productInfo?.blueprintId;
    const periodPrice = productInfo?.price.period.periodPrice;

    if (
      !blueprintVariant ||
      !blueprintGroupId ||
      !blueprintId ||
      !periodPrice
    ) {
      throw new Error('invalid session. Please reload the page!');
    }

    const browserInfo = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      javaEnabled: navigator.javaEnabled(),
      screenHeight: screen.height,
      screenWidth: screen.width,

      colorDepth: screen.colorDepth,
      timeZoneOffset: new Date().getTimezoneOffset(),
    };

    // generate returnUrl:
    // ATTENTION: This is a technically required url with processing at our end.
    // DO NOT REPLACE this url with a marketing-specific one since the payment might not be processed correctly then.
    const returnUrl = `${window.location.origin}/${PURCHASE_PAGE_CHECKOUT_VERIFY}?provider=adyen`;

    const environment = this.configuration;

    const body: ApiRequestPaymentDropinPay = {
      blueprintVariant,
      blueprintGroup: blueprintGroupId,
      blueprintId,
      periodPrice,
      channel: environment.production ? 'Web' : 'Web-test',
      selectedFeatureSet: null, // this.selectedBp.featureSet ? this.selectedBp.featureSet.id : null,
      invoiceCountryCode,
      returnUrl,
      shopperLocale: environment.languageArea.tag,
      browserInfo,
      dropinData: data,
    };

    let resp: ApiResponsePaymentDropinPay;
    let apiErrorObj: ApiError | null = null;

    try {
      resp = (await this.api.payment.dropin.pay(body, true)).response;
    } catch (err) {
      if (err instanceof ApiError) {
        console.log('api failed', {...err});
        if (!err.isApiError) throw err;

        resp = err.data as unknown as ApiResponsePaymentDropinPay;
        apiErrorObj = err;
      } else {
        throw err;
      }
    }

    console.log(resp);

    if (resp.success) {
      let fnRet: AdyenMakePaymentResult;

      if (resp.action) {
        fnRet = {
          complete: false,
          clientAction: 'provideDetails',
          resultCode: resp.resultCode,
          action: resp.action,
        };
      } else {
        // set successful payment info to session storage for tracking events
        if (resp.contractId) {
          const successfulPaymentInfo: SuccessfulPaymentInfo = {
            contractId: resp.contractId,
            amount: resp.amount,
            blueprintGroup: resp.blueprintGroup,
            blueprint: resp.blueprint,
            isTestEnvironment: resp.adyenTestEnvironment,
            operation: 'purchase',
          };
          Session.instance.setObject<SuccessfulPaymentInfo>(
            SessionInfo.purchase_successfulPaymentInfo,
            successfulPaymentInfo,
          );
        }

        fnRet = {
          complete: true,
          clientAction: 'none',
          resultCode: resp.resultCode,
          action: resp.action,
        };
      }

      return fnRet;
    } else {
      if (apiErrorObj) throw apiErrorObj;

      throw new Error(resp.message_localized);
    }
  }

  public async adyenProvideDetails(
    data: AdditionalDetailsData['data'],
    blueprintVariantOverride?: string,
    requestContractDetailsId?: string,
  ): Promise<AdyenMakePaymentResult> {
    const blueprintVariantSession = Session.instance.get(
      SessionInfo.purchase_blueprintVariant,
    );

    const blueprintVariant =
      blueprintVariantOverride ?? blueprintVariantSession;
    if (!blueprintVariant) {
      throw new Error('invalid session. Please reload the page!');
    }

    const environment = this.configuration;

    const body: ApiRequestPaymentDropinPaymentDetails = {
      channel: environment.production ? 'Web' : 'Web-test',
      dropinData: data,
      blueprintVariant,
    };

    if (requestContractDetailsId) {
      body.requestContractDetails = true;
      body.contractId = requestContractDetailsId;
    }

    let resp: ApiResponsePaymentDropinPaymentDetails;
    let fnRet: AdyenMakePaymentResult;
    let apiErrorObj: ApiError | null = null;

    try {
      resp = (await this.api.payment.dropin.paymentDetails(body, true))
        .response;
    } catch (err) {
      if (err instanceof ApiError) {
        console.log('api failed', {...err});

        resp = err.data as unknown as ApiResponsePaymentDropinPaymentDetails;
        apiErrorObj = err;
      } else {
        throw err;
      }
    }

    console.log(resp);

    if (resp.success) {
      if (resp.action) {
        fnRet = {
          complete: false,
          clientAction: 'provideDetails',
          resultCode: resp.resultCode,
          action: resp.action,
        };
      } else {
        // set successful payment info to session storage for tracking events
        if (requestContractDetailsId && resp.contractId) {
          const successfulPaymentInfo: SuccessfulPaymentInfo = {
            contractId: resp.contractId,
            amount: resp.amount,
            blueprintGroup: resp.blueprintGroup,
            blueprint: resp.blueprint,
            isTestEnvironment: resp.adyenTestEnvironment,
            operation: 'purchase',
          };
          Session.instance.setObject<SuccessfulPaymentInfo>(
            SessionInfo.purchase_successfulPaymentInfo,
            successfulPaymentInfo,
          );
        }

        fnRet = {
          complete: true,
          clientAction: 'none',
          resultCode: resp.resultCode,
          action: resp.action,
        };
      }

      return fnRet;
    } else {
      if (apiErrorObj) throw apiErrorObj;

      throw new Error(resp.message_localized);
    }
  }

  /**
   * Keep the backend "warm" to execute further requests faster
   * ATTENTION! Do not await this function. It will never resolve!
   */
  public async keepAwakeLoop(): Promise<void> {
    if (this.keepAwakeLoopRunning) {
      return;
    }

    this.keepAwakeLoopRunning = true;

    while (this.keepAwakeLoopRunning) {
      try {
        await this.api.payment.dropin.keepAwake({}, false);
      } finally {
        await new Promise<void>(resolve => setTimeout(() => resolve(), 10000));
      }
    }
  }
}
